import * as React from 'react';
import { ReactNode, useId } from 'react';
import { styled } from 'styled-components';
import Icon from './Icon';

interface CheckboxFieldProps {
	label: ReactNode;
	checked: boolean;
	onChange?: (checked: boolean) => void;
	onClick?: (e: React.MouseEvent<HTMLInputElement | HTMLSpanElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLSpanElement>) => void;
	id?: string;
	name?: string;
	className?: string;
	dataQaId?: string;
	hasError?: boolean;
	errorMessage?: string;
	// For accessibility
	'aria-checked'?: boolean;
	'aria-label'?: string;
}

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
	border: 0;
	clip: rect(0 0 0 0);
	clippath: inset(50%);
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	white-space: nowrap;
	width: 1px;
`;

const CheckboxVisual = styled.span<CheckboxVisualProps>`
	display: flex;
	flex: 0 0 auto;
	width: 20px;
	height: 20px;
	border: 1px solid var(--Grey-40, #d3d3d3);
	border-radius: 4px;
	align-items: center;
	justify-content: center;
	position: relative;
	background: ${({ $checked, $hasError }) => {
		if ($hasError && $checked) return 'var(--Red-60, #EA4E3C)';
		if ($hasError && !$checked) return 'var(--Red-10, #FFE9E9)';
		return $checked ? 'var(--Primary-squla-dark-blue, #0F0F41)' : 'white';
	}};
	border-color: ${({ $checked, $hasError }) => {
		if ($hasError) return 'var(--Red-60, #EA4E3C)';
		return $checked ? 'var(--Primary-squla-dark-blue, #0F0F41)' : 'var(--Grey-40, #d3d3d3)';
	}};
	outline: none;
`;

const CheckboxLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 400;
	cursor: pointer;
	position: relative;

	/* Show focus ring on label when input is focused */
	${HiddenCheckbox}:focus-visible + ${CheckboxVisual} {
		outline: 3px solid var(--Red-80, #DF3522);
		outline-offset: 2px;
	}
`;

interface CheckboxVisualProps {
	$checked: boolean;
	$hasError?: boolean;
}

const CheckIcon = styled(Icon)`
	display: inline-flex;
`;

const LabelText = styled.span`
	flex: 1 1 0;
	color: inherit;
`;

const FieldWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldError = styled.div`
	color: var(--Red-80);
	font-size: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

export function CheckboxField({
	ref,
	label,
	checked,
	onChange,
	onClick,
	onBlur,
	onKeyDown,
	id,
	className,
	dataQaId,
	name,
	hasError,
	errorMessage,
}: CheckboxFieldProps & { ref?: React.RefObject<HTMLInputElement | null> }) {
	const generatedId = useId();
	const checkboxId = id ?? generatedId;
	const errorId = `${checkboxId}-error`;

	return (
		<FieldWrapper>
			<CheckboxLabel htmlFor={checkboxId} className={className} data-qa-id={dataQaId}>
				<HiddenCheckbox
					ref={ref}
					id={checkboxId}
					name={name}
					checked={checked}
					aria-checked={checked}
					aria-label={typeof label === 'string' ? label : undefined}
					aria-invalid={hasError ? 'true' : 'false'}
					aria-describedby={errorMessage ? errorId : undefined}
					onChange={e => {
						if (onChange) {
							onChange(e.target.checked);
						}
					}}
					onClick={onClick}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
				/>
				<CheckboxVisual $checked={checked} $hasError={hasError} aria-hidden="true">
					{checked && <CheckIcon name="check-white" aria-hidden="true" color="#fff" />}
				</CheckboxVisual>
				<LabelText aria-hidden="true">{label}</LabelText>
			</CheckboxLabel>
			{errorMessage ? (
				<FieldError id={errorId} role="alert">
					<Icon name="alert-triangle" aria-hidden="true" size={16} />
					<span>{errorMessage}</span>
				</FieldError>
			) : null}
		</FieldWrapper>
	);
}

export type { CheckboxFieldProps };
export default CheckboxField;
